import { supabase } from "@/integrations/supabase/client";

export interface PDFConversionResult {
  success: boolean;
  text?: string;
  originalUrl?: string;
  error?: string;
}

export interface WordToPDFResult {
  success: boolean;
  pdfUrl?: string;
  originalUrl?: string;
  error?: string;
}

export const convertWordToPDF = async (fileUrl: string): Promise<WordToPDFResult> => {
  try {
    console.log('Converting Word to PDF:', fileUrl);

    // Call edge function which uses server-side CONVERTAPI_SECRET
    const { data, error } = await supabase.functions.invoke('convert-word-to-pdf', {
      body: {
        fileUrl: fileUrl,
        fileName: fileUrl.split('/').pop() || 'document.docx'
      }
    });

    if (error) {
      console.error('Edge function error:', error);
      throw new Error(error.message || 'Edge function failed');
    }

    if (!data?.success || !data?.pdfUrl) {
      throw new Error(data?.error || 'PDF conversion failed');
    }

    console.log('Word to PDF conversion successful:', data.pdfUrl);

    return {
      success: true,
      pdfUrl: data.pdfUrl,
      originalUrl: fileUrl
    };

  } catch (error) {
    console.error('Error converting Word to PDF:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to convert Word to PDF'
    };
  }
};

export const convertPDFToText = async (fileUrl: string): Promise<PDFConversionResult> => {
  try {
    console.log('Converting PDF to text:', fileUrl);

    // We bypass `supabase.functions.invoke` and use a direct fetch so we
    // can read the response BODY even when the edge function returns a
    // 4xx/5xx. The SDK's invoke() collapses non-2xx responses into a
    // generic "Edge Function returned a non-2xx status code" error with
    // the actual error message buried in a streamed context body — which
    // makes "PDF parsing failed" impossible to diagnose. Direct fetch
    // gives us the real error string from the function.
    const SUPABASE_URL = 'https://gxatnnzwaggcvzzurgsq.supabase.co';
    const { data: { session } } = await supabase.auth.getSession();
    const accessToken = session?.access_token;

    const res = await fetch(`${SUPABASE_URL}/functions/v1/convert-pdf-to-text`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: JSON.stringify({ fileUrl }),
    });

    // Always try to read the body — both success and error paths return
    // structured JSON with an `error` or `text` field.
    let body: any = null;
    try { body = await res.json(); } catch { /* non-JSON */ }

    if (!res.ok) {
      const detail = body?.error || `convert-pdf-to-text returned HTTP ${res.status}`;
      console.error('Edge function error body:', body);
      throw new Error(detail);
    }

    if (!body?.success) {
      throw new Error(body?.error || 'PDF to text conversion failed');
    }

    return {
      success: true,
      text: body.text,
      originalUrl: fileUrl
    };

  } catch (error) {
    console.error('Error converting PDF to text:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to convert PDF'
    };
  }
};
